// CarbonBridgeTestPlanDoc.tsx
import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Container,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CodeBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component={Paper}
    variant="outlined"
    sx={{
      p: 2,
      bgcolor: "grey.900",
      color: "grey.100",
      fontFamily: "monospace",
      fontSize: 13,
      overflowX: "auto",
      borderRadius: 2,
    }}
  >
    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
      <code>{children}</code>
    </pre>
  </Box>
);

const CarbonBridgeTestPlanDoc: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={4}>
        {/* Title */}
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Comprehensive test strategy
          </Typography>
        </Box>
        {/* Header */}
        <Box>
          <Typography variant="body2" color="text.secondary">
            Comprehensive test strategy for the LLM-governed carbon credit engine,
            aligned with international testing, quality, security, and AI-risk
            standards.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
            <Chip
              label="ISO/IEC/IEEE 29119 – Software Testing"
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label="ISO/IEC 25010 – Quality Model"
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label="NIST AI RMF 1.0 – AI Risk"
              size="small"
              color="secondary"
              variant="outlined"
            />
            <Chip
              label="OWASP ASVS – App Security"
              size="small"
              color="default"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Standards Alignment */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            0. Standards & Industry Alignment
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This test strategy borrows structure and terminology from:
          </Typography>
          <List dense sx={{ mb: 1 }}>
            <ListItem>
              <ListItemText
                primary="ISO/IEC/IEEE 29119 – Software Testing"
                secondary="Provides common vocabulary, test process layers (organizational, management, dynamic testing) and documentation types (test plan, test design, test cases)."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="ISO/IEC 25010 – Software Product Quality"
                secondary="Used to define non-functional quality goals for CarbonBridge (reliability, security, performance, maintainability, usability, portability)."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="NIST AI Risk Management Framework (AI RMF 1.0)"
                secondary="Guides the evaluation of the Extractor and Enforcer LLMs for robustness, explainability, reliability, and risk controls across lifecycle."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="OWASP Application Security Verification Standard (ASVS)"
                secondary="Provides a baseline checklist for web/API security verification of the CarbonBridge backend, admin UI, and public APIs."
              />
            </ListItem>
          </List>
          <Typography variant="body2">
            Test artefacts (plans, cases, reports) follow the spirit of 29119’s
            documentation structure, while quality attributes and acceptance
            criteria trace back to ISO 25010 & ASVS controls for security.
          </Typography>
        </Paper>

        {/* Accordion 1 – Test Strategy Overview */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight={600}>
              1. Test Strategy Overview (All Environments)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Objectives */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  1.1 Objectives
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Verify that all flows (mint, price, retire, bridge credits) behave correctly under valid and invalid inputs." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Ensure LLM-based policy enforcement (Extractor + Enforcer) makes consistent, explainable and reversible decisions for compliance-critical actions." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Validate that carbon credits are created, priced, and retired according to your rule set (1 credit = 1 tCO₂e, no double-counting, provenance and auditability preserved)." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Demonstrate that security controls (auth, authorisation, input validation) meet OWASP ASVS baseline for web/API security." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Provide audit-ready evidence that the platform is testable and traceable in line with ISO 29119 process expectations and ISO 25010 quality characteristics." />
                  </ListItem>
                </List>
              </Box>

              {/* Scope & Test Items */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  1.2 Scope & Test Items
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  The following components are in scope:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="LLM Extractor service – normalises user/REST input into structured requests." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="LLM Enforcer service – applies carbon credit rules and outputs approve/reject/needs_more_data." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Policy Gateway API – FastAPI/Node layer orchestrating Extractor, Enforcer, DB, and chain calls." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Smart contracts – ERGON/CARBON ERC-20, credit NFT contracts, bridge & verifier contracts on private/public chains." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Device ingestion – MQTT broker, HEPEK/device readings, persistence into measurement DB." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Explorers & dashboards – CarbonBridge explorer, admin console, audit logs." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Authentication & authorisation – admin, issuer, verifier, buyer roles on the platform." />
                  </ListItem>
                </List>
              </Box>

              {/* Test Levels & Types */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  1.3 Test Levels & Types
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  In line with ISO/IEC/IEEE 29119’s test process layering, you
                  apply:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Unit Tests"
                      secondary="Smart contracts (Solidity tests), backend services (API handlers, adapters, validators), LLM adapters (prompt builders & response parsers as pure functions)."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Integration Tests"
                      secondary="Extractor ↔ Enforcer ↔ DB ↔ chain; MQTT ingestion ↔ DB; bridge flows across chains; enforcement decisions vs contract actions."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="System / End-to-End Tests"
                      secondary="User-level flows: mint-from-energy; price-and-list credits; retire credits; bridge tokens/credits; replay of historical logs."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Non-Functional Tests"
                      secondary="Performance and load, reliability/resilience, security (OWASP ASVS), auditability, observability, and maintainability aligned with ISO 25010 quality attributes."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="AI-Specific Tests"
                      secondary="Consistency, robustness, and explainability checks for Extractor/Enforcer prompts, guided by NIST AI RMF (risk identification, measurement, mitigation)."
                    />
                  </ListItem>
                </List>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 2 – Detailed Test Plan: Standard Scenario */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight={600}>
              2. Detailed Test Plan – Standard Scenario (Single SME, Multi-Device)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Scenario description */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  2.1 Scenario Description
                </Typography>
                <Typography variant="body2">
                  This scenario covers a typical deployment: a single SME with
                  several HEPEK/inverter devices on one private chain, with NFT
                  credits minted and retired on a connected public chain (no
                  cross-bridge complexity beyond simple ERGON/CARBON transfers).
                </Typography>
              </Box>

              {/* Test Matrix */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  2.2 Test Scenario Matrix
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  High-level view of the main test scenarios:
                </Typography>
                <CodeBlock>
                  {`Scenario ID | Area                | Description
-----------|---------------------|---------------------------------------------------------
TS-001     | Minting – Happy Path | Device kWh → Extractor → Enforcer (approve) → NFT mint
TS-002     | Minting – Overclaim  | Requested credits > energy-based max → Enforcer rejects
TS-003     | Minting – Unknown Dev| Non-whitelisted device → Enforcer returns needs_more_data/reject
TS-004     | Pricing – Benchmark  | Price/credit checked vs external carbon benchmark
TS-005     | Retirement – Owner   | Credit owner retires; on-chain state & logs updated
TS-006     | Retirement – Invalid | Non-owner attempts to retire → transaction rejected
TS-007     | MQTT Ingestion       | Device publishes readings; broker → DB; data available for mint
TS-008     | Auth & Roles         | Admin vs issuer vs buyer permissions enforced (ASVS auth controls)`}
                </CodeBlock>
              </Box>

              {/* Functional Tests */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  2.3 Functional Test Coverage
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Extractor: correctly parses device IDs, kWh, emission factors, and requested credits from API payloads." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Enforcer: recomputes CO₂e from kWh and emission factor; enforces 1 credit = 1 tCO₂e; returns appropriate decision and compliance score." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Policy Gateway: only executes on-chain mint/retire when Enforcer decision is approve and score ≥ configured threshold." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Smart contracts: enforce total supply, burning, and retirement semantics; events emitted exactly once per action." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="MQTT/REST integration: energy data used for minting comes only from persisted measurements, never from ad-hoc manual entries." />
                  </ListItem>
                </List>
              </Box>

              {/* Non-Functional */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  2.4 Non-Functional & Quality Testing (ISO 25010)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Performance & Scalability"
                      secondary="Load tests for high-frequency device readings, concurrent mint/retire requests, and multiple LLM calls per second."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Reliability & Recoverability"
                      secondary="Simulate broker downtime, chain node restart, or LLM outage; verify graceful degradation and recovery without data loss."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Security (OWASP ASVS)"
                      secondary="Verify authN/authZ, rate limiting, input validation, secure session handling, and correct secrets handling for LLM/API keys."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Maintainability & Observability"
                      secondary="Check logs, metrics, and traces available for all critical paths (mint, retire, bridge, enforcement) for post-incident analysis."
                    />
                  </ListItem>
                </List>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 3 – Compliance & Audit Plan: Complex Scenario */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" fontWeight={600}>
              3. Compliance & Audit-Focused Plan – Complex Scenario
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={3}>
              {/* Scenario description */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  3.1 Scenario Description
                </Typography>
                <Typography variant="body2">
                  This scenario reflects a production-grade deployment with
                  multiple organisations, devices in several regions, multi-chain
                  bridging (private ⇄ public), and heavy regulatory/audit
                  expectations. It emphasises traceability, evidence, and AI-risk
                  control.
                </Typography>
              </Box>

              {/* Compliance Objectives */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  3.2 Compliance & Evidence Objectives
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="For each credit, prove a full chain: measurement → tokenisation → credit → retirement, with non-repudiable records." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Demonstrate that no measurement interval is re-used for multiple credits or environmental products (zero double-counting)." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Show that AI-based decisions (Extractor/Enforcer) are logged with inputs, parsed facts, decisions, and reasons for audit sampling." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Provide documented evidence of security testing mapped to OWASP ASVS controls and relevant ISO/IEC 27001-style controls where applicable." />
                  </ListItem>
                </List>
              </Box>

              {/* AI Risk & LLM Testing */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  3.3 AI Risk & LLM Behaviour Testing (NIST AI RMF)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Based on NIST AI RMF, tests cover:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Robustness: give adversarial or malformed payloads (e.g., asking to bypass limits) and ensure Enforcer never approves non-compliant actions." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Reliability: repeated evaluation of the same request yields the same decision and reasoning (within acceptable tolerance)." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Explainability: decisions include clear reasoning fields that can be presented to internal reviewers and regulators." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Governance & Monitoring: ensure that LLM prompts and models are versioned, with regression test suites for each model/prompt update." />
                  </ListItem>
                </List>
              </Box>

              {/* Cross-chain & Freeze–Burn Testing */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  3.4 Blockchain & Cross-Chain Integrity
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  This plan validates advanced blockchain rules:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Freeze–burn semantics: tokens used for credit minting are frozen and then burned/locked after successful mint, preventing double-spend." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Cross-chain bridging: credits/tokens on target chain cannot exist in parallel as live, spendable assets on source chain for the same underlying climate benefit." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Device-origin signature checks: credit metadata includes device wallet signature or proof-of-origin; tampered metadata fails validation." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Reconciliation tests: periodically reconcile on-chain totals with off-chain measurement DB to detect drift or corruption." />
                  </ListItem>
                </List>
              </Box>

              {/* Audit Logs & Reporting */}
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  gutterBottom
                >
                  3.5 Audit Logs, Reporting & Traceability
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  For each critical action, test that:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="Policy decision (Extractor+Enforcer input/outputs) is logged with correlation ID and minimal PII." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="On-chain tx hash is recorded alongside the off-chain policy decision and device measurements." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Standard test reports follow ISO 29119-style templates for test summary, incident reports, and coverage metrics." />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Exportable evidence packs (PDF/CSV) can be generated for sampling by auditors or regulators." />
                  </ListItem>
                </List>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Container>
  );
};

export default CarbonBridgeTestPlanDoc;
